import { type AxiosError } from 'axios'
import { type SubmitEventHandler, useCallback, useState } from 'react'
import { type SubmitHandler, type UseFormHandleSubmit } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { HTTP_STATUS_CODE_BAD_REQUEST } from '../../../http'
import { useOneTimePasswordApiLoginMutation } from '../../api/swr/useOneTimePasswordApiLoginMutation'
import {
  type LoginAfterLoggedIn,
  makeLoginAfterLoggedInForMethod,
} from '../../loginAfterLoggedIn'

import { type LoginRegistrationOneTimePasswordInputFormData } from './loginRegistrationOneTimePasswordInputFormData'

interface UseLoginRegistrationOneTimePasswordSubmissionParameters {
  afterLoggedIn?: LoginAfterLoggedIn
  handleSubmit: UseFormHandleSubmit<LoginRegistrationOneTimePasswordInputFormData>
  onAcceptedSubmit?: () => void
  onSubmissionFailure?: () => void
  username: string
}

interface LoginRegistrationOneTimePasswordSubmission {
  apiErrorMessage: string | null
  handleFormSubmit: SubmitEventHandler<HTMLFormElement>
  isMutating: boolean
  mutationError: unknown
  showGeneralErrorNotification: boolean
}

export const useLoginRegistrationOneTimePasswordSubmission = ({
  afterLoggedIn,
  handleSubmit,
  onAcceptedSubmit,
  onSubmissionFailure,
  username,
}: UseLoginRegistrationOneTimePasswordSubmissionParameters): LoginRegistrationOneTimePasswordSubmission => {
  const intl = useIntl()
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null)
  const [showGeneralErrorNotification, setShowGeneralErrorNotification] =
    useState(false)
  const otpSwr = useOneTimePasswordApiLoginMutation({
    onError: (error: AxiosError) => {
      onSubmissionFailure?.()
      if (error.response?.status === HTTP_STATUS_CODE_BAD_REQUEST) {
        setApiErrorMessage(intl.formatMessage({ id: 'login.otp.wrongCode' }))
      } else {
        setShowGeneralErrorNotification(true)
      }
    },
    onSuccess: makeLoginAfterLoggedInForMethod(afterLoggedIn, 'otp'),
  })
  const submit: SubmitHandler<LoginRegistrationOneTimePasswordInputFormData> =
    useCallback(
      async (formData): Promise<void> => {
        if (otpSwr.isMutating) {
          return
        }

        onAcceptedSubmit?.()
        await otpSwr.trigger(
          {
            fcSolution: formData.fcSolution,
            password: formData.password,
            username,
          },
          { throwOnError: false },
        )
      },
      [onAcceptedSubmit, otpSwr, username],
    )
  const handleFormSubmit: SubmitEventHandler<HTMLFormElement> = useCallback(
    (event): void => {
      setShowGeneralErrorNotification(false)
      setApiErrorMessage(null)
      void handleSubmit(submit)(event)
    },
    [handleSubmit, submit],
  )

  return {
    apiErrorMessage,
    handleFormSubmit,
    isMutating: otpSwr.isMutating,
    mutationError: otpSwr.error,
    showGeneralErrorNotification,
  }
}
